import AjaxCall from '../ws/ajax-call';
import AjaxUrls from '../constants/ajax-url';
import TrackingAction from '../constants/tracking-action';
import ParamStore from '../logic/stores/param-store';
import JmServices from '../ws/jm-services';
import ErrorLogger from '../infrastructure/error-logger';
import RedirectUrl from '../constants/redirect-url';
import ProcessType from '../constants/process-type';

export default class ClsServices {

    static get clsScope() {
        return 'openid offline_access';
    }

    static get _clsGrantType() {
        return 'code';
    }

    static register(componentId, options) {
        try {
            if (!options.email
                || options.activate === undefined || options.activate === null
                || options.keepLoggedIn === undefined || options.keepLoggedIn === null
                || options.sendProductInformation === undefined || options.sendProductInformation === null) {
                throw new Error(`JLS Missing ClsServices.register preconditions; Options: ${JSON.stringify(options)}`);
            }

            return AjaxCall.call({
                isAsync: false,
                url: AjaxUrls.cls.register.url.replace('{clsAbsUrl}', ParamStore.getModel(componentId).clsEndpoint),
                method: AjaxUrls.cls.register.method,
                requestData: {
                    username: options.email,
                    password: options.password,
                    auto_login: !!options.keepLoggedIn,
                    auto_email: !!options.sendProductInformation,
                    client_id: ParamStore.getModel(componentId).clsClientId,
                    redirect_uri: ParamStore.getModel(componentId).clsReturnUrl,
                    ui_locales: ParamStore.getModel(componentId).lang5,
                    scope: ClsServices.clsScope,
                    grant_type: ClsServices._clsGrantType,
                    activate_email: !!options.activate
                }
            }).then(
                (responseData) => { // onFulfilled

                    try {
                        if (ParamStore.getModel(componentId).callbacks.successfulRegistrationAtClsSide) {
                            ParamStore.getModel(componentId).callbacks.successfulRegistrationAtClsSide();
                        }

                        JmServices.trackEvent(componentId, {trackingValue: TrackingAction.jlsSuccessfulRegistrationAtClsSide});

                        if (!options.sendProductInformation) {
                            JmServices.trackEvent(componentId, {trackingValue: TrackingAction.jlsSuccessfulRegistrationAtClsSideSendMeProductInformationUnchecked});
                        }

                        ClsServices._executeResponseForm(componentId, responseData, options.email, ProcessType.register);
                    } catch (ex) {
                        JmServices.trackEvent(componentId, {trackingValue: TrackingAction.jlsFailedRegistrationAtClsSide});
                        ErrorLogger.log(ex);

                        if (ParamStore.getModel(componentId).callbacks.registration) {
                            ParamStore.getModel(componentId).callbacks.registration({ email: options.email, data: responseData});
                        }

                        return {error: true};
                    }
                },
                (responseData) => { // onRejected
                    JmServices.trackEvent(componentId, {trackingValue: TrackingAction.jlsFailedRegistrationAtClsSide});

                    if (ParamStore.getModel(componentId).callbacks.registration) {
                        ParamStore.getModel(componentId).callbacks.registration({ email: options.email, data: responseData});
                    }

                    return {error: true};
                }
            )
        }
        catch (ex) {
            ErrorLogger.log(ex);
            throw ex;
        }
    }

    static login(componentId, options) {
        try {
            if (!options.email || !options.password || !options.clsProviderType ||
                options.keepLoggedIn === undefined || options.keepLoggedIn === null) {
                options.password = '';
                throw new Error(`JLS Missing ClsServices.login preconditions; Options: ${JSON.stringify(options)}`);
            }

            return AjaxCall.call({
                isAsync: false,
                url: AjaxUrls.cls.login.url.replace('{clsAbsUrl}', ParamStore.getModel(componentId).clsEndpoint),
                method: AjaxUrls.cls.login.method,
                requestData: {
                    username: options.email,
                    password: options.password,
                    auto_login: options.keepLoggedIn,
                    provider: options.clsProviderType,
                    client_id: ParamStore.getModel(componentId).clsClientId,
                    redirect_uri: ParamStore.getModel(componentId).clsReturnUrl,
                    ui_locales: ParamStore.getModel(componentId).lang5,
                    scope: ClsServices.clsScope,
                    grant_type: ClsServices._clsGrantType,
                }
            }).then(
                (responseData) => { // onFulfilled

                    try {
                        if (ParamStore.getModel(componentId).callbacks.successfulLoginAtClsSide) {
                            ParamStore.getModel(componentId).callbacks.successfulLoginAtClsSide();
                        }

                        // TODO: Tracking
                        ClsServices._executeResponseForm(componentId, responseData, options.email, ProcessType.login);
                    } catch (ex) {
                        // TODO: Tracking
                        ErrorLogger.log(ex);

                        return {error: true, generic: true};
                    }
                },
                () => { // onRejected
                    // TODO: Tracking
                    return {error: true, invalidCredentials: true};
                }
            )
        }
        catch (ex) {
            ErrorLogger.log(ex);
            throw ex;
        }
    }

    static canAutoLogin(componentId) {
        try {
            var paramStoreModel = ParamStore.getModel(componentId);

            return AjaxCall.call({
                isAsync: false,
                url: AjaxUrls.cls.tryAutoLogin.url.replace('{clsAbsUrl}', paramStoreModel.clsEndpoint),
                method: AjaxUrls.cls.tryAutoLogin.method,
                requestData: {}
            }).then(
                (responseData) => { // onFulfilled
                    JmServices.trackEvent(componentId, {trackingValue: TrackingAction.jlsSuccessfulTryAutoLoginAtClsSide});
                    return {canAutoLogin: !!responseData.is_auto_login_available};
                },
                (responseData) => { // onRejected
                    JmServices.trackEvent(componentId, {trackingValue: TrackingAction.jlsFailedTryAutoLoginAtClsSide});
                    return {canAutoLogin: false};
                }
            )
        }
        catch (ex) {
            JmServices.trackEvent(componentId, {trackingValue: TrackingAction.jlsFailedTryAutoLoginAtClsSide});
            ErrorLogger.log(ex);
            throw ex;
        }
    }

    static autoLogin(componentId) {
        try {
            var paramStoreModel = ParamStore.getModel(componentId);

            return ClsServices.canAutoLogin(componentId)
                .then((result) => {
                    if(!result.canAutoLogin) {
                        return result;
                    }

                    // This is very NOT the seamless implementation
                    const autoLoginUrl =
                        RedirectUrl.cls.autoLogin
                            .replace('{clsAbsUrl}', paramStoreModel.clsEndpoint)
                            .replace('{clsClientId}', paramStoreModel.clsClientId)
                            .replace('{clsReturnUrl}', paramStoreModel.clsAutoLoginReturnUrl)
                            .replace('{lang5}', paramStoreModel.lang5);

                    window.top.location = autoLoginUrl;
                });
        }
        catch (ex) {
            JmServices.trackEvent(componentId, {trackingValue: TrackingAction.jlsFailedAutoLoginAtClsSide});
            ErrorLogger.log(ex);
            throw ex;
        }
    }

    static isEmailExisting(componentId, options) {
        try {
            if (!options.email) {
                throw new Error(`JLS Missing ClsServices.isEmailExisting preconditions; Options: ${JSON.stringify(options)}`);
            }

            return AjaxCall.call({
                isAsync: false,
                url: AjaxUrls.cls.isEmailExists.url.replace('{clsAbsUrl}', ParamStore.getModel(componentId).clsEndpoint),
                method: AjaxUrls.cls.isEmailExists.method,
                requestData: {
                    username: options.email,
                    client_id: ParamStore.getModel(componentId).clsClientId
                }
            })
        }
        catch (ex) {
            ErrorLogger.log(ex);
            throw ex;
        }
    }

    static isEmailFederated(componentId, options) {
        try {
            if (!options.email) {
                throw new Error(`JLS Missing ClsServices.isDomainExisting preconditions; Options: ${JSON.stringify(options)}`);
            }

            return AjaxCall.call({
                isAsync: false,
                url: AjaxUrls.cls.isEmailFederated.url.replace('{clsAbsUrl}', ParamStore.getModel(componentId).clsEndpoint),
                method: AjaxUrls.cls.isEmailFederated.method,
                requestData: {
                    username: options.email,
                    client_id: ParamStore.getModel(componentId).clsClientId
                }
            })
        }
        catch (ex) {
            ErrorLogger.log(ex);
            throw ex;
        }
    }

    static _executeResponseForm(componentId, responseData, email, processType) {
        if (!responseData.form || responseData.error_type) {
            throw new Error(`JLS Missing ClsServices._executeResponseForm preconditions; ResponseData: \
                ${JSON.stringify(responseData)}`);
        }

        let parameters = ParamStore.getModel(componentId),
            clsExecutionArea = document.getElementById(parameters.clsExecutionArea),
            // We need this to differentiate between instances and CLS processes within a single instance
            uniqueProcessId = componentId + '-' + new Date().getTime();

        if (!clsExecutionArea) {
            // Fall back to document body if running only at core level
            clsExecutionArea = document.body;
        }

        // Render CLS Form into DOM
        // TODO: Workaround to fix rendering clsForm.target
        clsExecutionArea.innerHTML += `<div id='cls-form-container-${uniqueProcessId}'\
                                        style='display: none'>${responseData.form}<div>`;

        if (parameters.silentMode) {
            if (processType === ProcessType.login) {
                // Do not allow login in silent mode because many CLS flow renders end up in CLS side
                throw new Error ('Login flow cannot be processed in silent Mode')
            }

            clsExecutionArea.innerHTML +=
                `<iframe style='width: 1px; height: 1px; visibility: hidden'\
                         data-componentId='${componentId}'\
                         data-email='${email}'\
                         data-process-type='${processType}'\
                         id='silent-cls-operation-${uniqueProcessId}'\
                         name='silent-cls-operation-${uniqueProcessId}'></iframe>`;

            // Change Form POST target
            // Note: Do not use existing references to the form to avoid chrome issues like:
            // Form submission canceled because the form is not connected
            document.getElementById(`cls-form-container-${uniqueProcessId}`).firstChild.target=
                `silent-cls-operation-${uniqueProcessId}`;

            // Handle iframe onload
            document.getElementById(`silent-cls-operation-${uniqueProcessId}`).onload = (event) => {
                let componentId = event.target.getAttribute('data-componentId'),
                    email = event.target.getAttribute('data-email'),
                    processType = event.target.getAttribute('data-process-type'),
                    paramStore = jwfLogin.paramStore(componentId);

                // Remove unused resources
                // TODO: This is OK only for Chrome
                event.target.remove();
                document.getElementById(`cls-form-container-${uniqueProcessId}`).remove();

                if (processType === jwfLogin.ProcessType.register.toString() &&
                    paramStore.callbacks && paramStore.callbacks.registration) {
                    paramStore.callbacks.registration({ email: email, data: 'OK'});
                }

                if (processType === jwfLogin.ProcessType.tryAutoLogin.toString() &&
                    paramStore.callbacks && paramStore.callbacks.tryAutoLogin) {
                    // TODO: Call tryAutoLogin callback in proper way
                }
            };
        }

        // Submit form
        // Note: Do not use existing references to the form to avoid chrome issues like:
        // Form submission canceled because the form is not connected
        document.getElementById(`cls-form-container-${uniqueProcessId}`).firstChild.submit();
    }
}


/** WEBPACK FOOTER **
 ** ./src/ws/cls-services.js
 **/